import React, { createContext } from 'react';
import { useRouter } from 'next/router';
import useCustomInit from '@/hooks/custom/useCustomInit';
import ErrorHandler from '../ErrorHandler';
import { OverseasAgentMongooseClass } from '@/types/apollo';

export interface CodeType {
  code: string;
  name: string;
  isDirectInput: boolean;
}

export interface CustomOption {
  groupType: string;
  groupName: string;
  groupDescription: string;
  specialNotes: CodeType[];
  mealOrShoppings: CodeType[];
  additionalRequirementCheckList: CodeType[];
}

export interface GroupByVisitArea {
  countryCode: string;
  countryName: string;
  cities: {
    cityCode: string;
    cityName: string;
  }[];
}

export interface CustomProduct {
  agtCode: string;
  productId: string;
  productFamilyId: string;
  productName: string;
  beginDate: string;
  endDate: string;
  productFamily: {
    images: string[];
    visitAreas: GroupByVisitArea[];
    trafficAir?: {
      detail?: {
        departures?: {
          airlineName?: string;
        }[];
      };
    };
  };
  overseasBookMinSeat: number;
}

export interface CustomInitContextProps {
  customAvailable: boolean;
  customOptions: CustomOption[];
  customRequirements: CodeType[];
  overseasAgent: OverseasAgentMongooseClass | null;
  overseasProduct: CustomProduct | null;
}

export const CustomInitContext = createContext<CustomInitContextProps>({
  customAvailable: false,
  customOptions: [],
  customRequirements: [],
  overseasAgent: null,
  overseasProduct: null,
});

export default function CustomInitProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const { agtName, productId } = router.query;
  const { customAvailable, customOptions, overseasAgent, customRequirements, overseasProduct, loading } = useCustomInit(
    {
      landingUrl: agtName as string,
      productId: productId as string,
    },
  );

  if (loading) return null;

  const isError = !customAvailable && !loading;
  if (isError) {
    return <ErrorHandler isError={isError} />;
  }

  return (
    <CustomInitContext.Provider
      value={{ customAvailable, customOptions, customRequirements, overseasAgent, overseasProduct }}
    >
      {children}
    </CustomInitContext.Provider>
  );
}
